import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Gift } from "lucide-react";
import type { CartItemInput, Promo, SitePremiumItem, VoucherItemKind } from "@baze/api-client";
import { api } from "@/lib/api";
import { qk } from "@/lib/query";
import { formatRupiah } from "@/lib/format";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { useCart } from "./cart";

/** Featured vouchers (public; a signed-in user also learns whether they already used one). */
export function usePromos() {
  return useQuery({ queryKey: qk.commerce.promos, queryFn: () => api.commerce.promos(), staleTime: 60_000 });
}

/** The paid template / theme a website uses and whether each is unlocked. */
export function useSitePremium(siteId: string) {
  return useQuery({ queryKey: qk.commerce.premium(siteId), queryFn: () => api.commerce.sitePremium(siteId) });
}

/** The first promo this user can still use on any of these item kinds. */
export function promoFor(promos: Promo[] | undefined, kinds: VoucherItemKind[]): Promo | undefined {
  return promos?.find((p) => !p.usedByMe && (p.appliesTo.length === 0 || p.appliesTo.some((k) => kinds.includes(k))));
}

/** "Gratis" for a 100% promo, otherwise the voucher's own summary ("Diskon 50% untuk tema"). */
export function promoHeadline(p: Promo): string {
  return p.discountType === "percent" && p.discountValue >= 100 ? t.promo.free : p.summary;
}

export const premiumCartItem = (it: Pick<SitePremiumItem, "kind" | "id">): CartItemInput => ({ kind: it.kind, id: it.id });

/** Put items in the site's cart and open checkout, with the promo code already applied when there is one. */
export function useUnlock(siteId: string) {
  const cart = useCart();
  const navigate = useNavigate();
  return (items: CartItemInput[], promo?: Promo) => {
    cart.addMany(siteId, items);
    navigate(`/situs/${siteId}/checkout${promo ? `?voucher=${encodeURIComponent(promo.code)}` : ""}`);
  };
}

/** Compact promo line: gift icon, code, and what it gives. */
export function PromoCallout({ promo, className, action }: { promo: Promo; className?: string; action?: React.ReactNode }) {
  return (
    <div className={cn("flex items-start gap-3 rounded-xl bg-emerald-50 px-3.5 py-3 ring-1 ring-inset ring-emerald-200/80", className)}>
      <Gift className="mt-0.5 size-5 shrink-0 text-emerald-700" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-emerald-950">
          {t.promo.useCode(promo.code)} <span className="font-semibold text-emerald-800">· {promoHeadline(promo)}</span>
        </p>
        <p className="mt-0.5 text-[13px] leading-snug text-emerald-900/80">
          {promo.title || promo.summary}
        </p>
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}

export const formatPriceOrFree = (price: number) => (price > 0 ? formatRupiah(price) : t.common.free);
